import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDeadlines, getCustomerPriority } from '../hooks/useDeadlines';
import { CustomerDeadlineCard } from './CustomerDeadlineCard';
import { DeadlineFilters } from './DeadlineFilters';
import { EditCustomerModal } from '../../customers/components/EditCustomerModal';
import { CreateCustomerModal } from '../../customers/components/CreateCustomerModal';
import { isDeadlineSnoozed, getNachzueglerKey, getIndividualNachzueglerKey, ermittleRuhendeFristen } from '../../../shared/utils/deadlineUtils';
import { UploadModal } from './UploadModal';
import { ReleaseModal } from './ReleaseModal';
import { ZendeskWarningModal } from './ZendeskWarningModal';
import { SecureDocumentModal } from '../../secureDocuments/components/SecureDocumentModal';
import { LohnscheinVersandModal } from './LohnscheinVersandModal';
import { OppmQrVersandModal } from './OppmQrVersandModal';
import { MonatsuebersichtQrVersandModal } from './MonatsuebersichtQrVersandModal';
import { ScheduledFinalizationEmails } from './ScheduledFinalizationEmails';
import { Customer, CustomerDeadlineView } from '../../../shared/types';
import { useAuth } from '../../auth';
import { staffService } from '../../../services/staff/StaffService';

interface CategoryState {
  thisWeek: boolean;
  nextWeek: boolean;
  underControl: boolean;
}

// HILFSFUNKTIONEN AUSSERHALB DER KOMPONENTE (keine Dependencies nötig)
const getNextDeadlineDate = (view: CustomerDeadlineView): Date | null => {
  let earliestDeadline: Date | null = null;

  // Prüfe Standard-Deadlines
  const deadlinesToCheck: Array<{ date: Date | undefined; completed: boolean | undefined; fieldName: string }> = [
    { date: view.deadlines.lohnlauf_1, completed: view.status?.lohnlauf_1_completed, fieldName: 'lohnlauf_1_completed' },
    { date: view.deadlines.lohnlauf_2, completed: view.status?.lohnlauf_2_completed, fieldName: 'lohnlauf_2_completed' },
    { date: view.deadlines.lohnsteuer, completed: view.status?.lohnsteuer_completed, fieldName: 'lohnsteuer_completed' },
    { date: view.customer.sv_pflicht && view.deadlines.sv_beitraege ? view.deadlines.sv_beitraege : undefined, completed: view.status?.sv_beitraege_completed, fieldName: 'sv_beitraege_completed' },
    // Lohnschein-Versand an Arbeitnehmer (Zwei-Lauf-Modell), je Lauf ein Eintrag.
    // Erledigung kommt aus den View-Feldern, nicht aus dem Monatsstatus.
    { date: view.deadlines.lohnschein_versand_1, completed: view.lohnscheinCompleted1, fieldName: 'lohnschein_versand_1_completed' },
    { date: view.deadlines.lohnschein_versand_2, completed: view.lohnscheinCompleted2, fieldName: 'lohnschein_versand_2_completed' }
  ];

  // NACHZÜGLER: offene Lohnschein-Läufe des zuletzt abgeschlossenen Monats.
  // Müssen hier mit, damit Sortierung und Kategorie-Einteilung zusammenpassen —
  // sonst rutscht ein Kunde mit vergessenem Versand in der Liste nach unten,
  // obwohl er in der Gruppe "Sofortiger Handlungsbedarf" steht.
  (view.nachzuegler || []).forEach(n => {
    deadlinesToCheck.push({
      date: n.faelligkeit,
      completed: false,
      fieldName: getNachzueglerKey(n)
    });
  });

  // NACHZÜGLER INDIVIDUELLE FRISTEN: offene individuelle Fristen des zuletzt
  // abgeschlossenen Monats. Gleiche Begründung wie oben.
  (view.individualNachzuegler || []).forEach(n => {
    deadlinesToCheck.push({
      date: n.faelligkeit,
      completed: false,
      fieldName: getIndividualNachzueglerKey(n)
    });
  });

  // Füge individuelle Deadlines hinzu
  if (view.individualDeadlines && view.individualDeadlines.length > 0) {
    view.individualDeadlines.forEach(individualDeadline => {
      const deadlineKey = `individual_${individualDeadline.id}`;
      const deadlineDate = view.deadlines[deadlineKey];
      
      // Für einmalige Deadlines: nutze completed aus der Deadline selbst
      const isCompleted = !individualDeadline.is_recurring 
        ? individualDeadline.completed || false
        : view.status?.individual_deadlines_status?.[individualDeadline.id] || false;
      
      deadlinesToCheck.push({
        date: deadlineDate,
        completed: isCompleted,
        fieldName: `individual_deadline_${individualDeadline.id}`
      });
    });
  }

  // WARTET AUF KUNDEN: Ruhende Fristen zählen nicht als nächste Frist. Ein
  // Kunde, der nur wegen ruhender Fristen in "Sofortiger Handlungsbedarf"
  // steht, sortiert sich dadurch hinter die Kunden mit echtem Handlungsbedarf.
  const ruhendeFristen = ermittleRuhendeFristen(view.customer, view.status, view.individualDeadlines, view.lohnscheinRelativ);

  // Finde die nächste unerledigte Deadline
  // SNOOZE FEATURE: Bei aktiv gesnoozten Fristen wird snoozed_until als effektives
  // Datum verwendet (analog zu calculateCustomerPriority), damit die Sortierung
  // mit der Kategorie-Einteilung übereinstimmt.
  deadlinesToCheck.forEach(({ date, completed, fieldName }) => {
    if (!completed && date && !ruhendeFristen.has(fieldName)) {
      let effectiveDate = date;

      if (isDeadlineSnoozed(fieldName, view.status) && view.status?.snoozed_deadlines?.[fieldName]) {
        const snoozedUntil = new Date(view.status.snoozed_deadlines[fieldName].snoozed_until + 'T00:00:00');
        // Nutze snoozed_until nur wenn es nach dem Original-Datum liegt
        if (snoozedUntil > date) {
          effectiveDate = snoozedUntil;
        }
      }

      if (!earliestDeadline || effectiveDate < earliestDeadline) {
        earliestDeadline = effectiveDate;
      }
    }
  });

  return earliestDeadline;
};

const sortByNextDeadlineAndName = (a: CustomerDeadlineView, b: CustomerDeadlineView): number => {
  const dateA = getNextDeadlineDate(a);
  const dateB = getNextDeadlineDate(b);
  
  // Beide haben keine Deadline - alphabetisch sortieren
  if (!dateA && !dateB) {
    return a.customer.company.localeCompare(b.customer.company);
  }
  
  // Nur A hat keine Deadline - B zuerst
  if (!dateA) return 1;
  
  // Nur B hat keine Deadline - A zuerst
  if (!dateB) return -1;
  
  // Beide haben Deadlines - nach Datum sortieren
  const dateDiff = dateA.getTime() - dateB.getTime();
  
  // Wenn gleiche Deadline - alphabetisch sortieren
  if (dateDiff === 0) {
    return a.customer.company.localeCompare(b.customer.company);
  }
  
  return dateDiff;
};

export const DeadlineDashboard: React.FC = () => {
  const { user } = useAuth();
  const { 
    customerDeadlineViews, 
    loading, 
    loadData, 
    toggleStatus, 
    loadZendeskForCustomers,
    showUploadModal,
    showReleaseModal,
    currentFinalizationData,
    handleFileUpload,
    handleRelease,
    closeUploadModal,
    closeReleaseModal,
    resetCustomerMonth,
    showZendeskWarning,
    zendeskWarningData,
    handleZendeskProceed,
    handleZendeskCancel,
    isButtonLoading,
    snoozeDeadline,
    markDatenAnlieferung,
    unmarkDatenAnlieferung,
    markWartetAufKunden,
    unmarkWartetAufKunden,
    markKundenRueckmeldung,
    unmarkKundenRueckmeldung,
    showLohnscheinModal,
    lohnscheinVersandData,
    handleLohnscheinVersandSuccess,
    closeLohnscheinModal,
    openLohnscheinNachzuegler,
    openSpezialfristNachzuegler,
    erledigeIndividualNachzuegler,
    showOppmQrModal,
    oppmQrVersandData,
    handleOppmQrRelease,
    handleOppmQrMarkDone,
    closeOppmQrModal,
    showMonatsuebersichtQrModal,
    monatsuebersichtQrVersandData,
    handleMonatsuebersichtQrRelease,
    handleMonatsuebersichtQrMarkDone,
    closeMonatsuebersichtQrModal
  } = useDeadlines();
  
  const [expandedCategories, setExpandedCategories] = useState<CategoryState>({
    thisWeek: false,
    nextWeek: false,
    underControl: false
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  
  // useRef für Filter-Tracking - saubere Lösung ohne State für previous values
  const prevSearchTermRef = useRef<string>('');
  const prevSelectedStaffIdRef = useRef<string | null>(null);
  // Ref zum Tracken, für welche "andere Sachbearbeiter"-Treffer Zendesk bereits geladen wurde
  const otherStaffZendeskRef = useRef<string>('');
  
  // NEUE STATES für Edit-Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  
  // NEUE STATES für Create-Modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // NEUE STATES für SecureDocument-Modal
  const [showSecureDocumentModal, setShowSecureDocumentModal] = useState(false);
  const [secureDocumentCustomer, setSecureDocumentCustomer] = useState<Customer | null>(null);

  // Initialisiere den Filter mit dem angemeldeten Nutzer
  useEffect(() => {
    const initializeUserFilter = async () => {
      if (!user?.email || isInitialized) return;
      
      try {
        const staffMembers = await staffService.getActiveStaff();
        const currentUserStaff = staffMembers.find(
          staff => staff.email.toLowerCase() === user.email.toLowerCase()
        );
        
        if (currentUserStaff) {
          setSelectedStaffId(currentUserStaff.id);
          prevSelectedStaffIdRef.current = currentUserStaff.id; // Wichtig: Ref auch initialisieren
        }
      } catch (error) {
        console.error('Error initializing user filter:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeUserFilter();
  }, [user, isInitialized]);

  // Reset bei Mitarbeiterwechsel - Gruppen zurücksetzen für saubere Anzeige
  useEffect(() => {
    if (isInitialized && selectedStaffId !== prevSelectedStaffIdRef.current && prevSelectedStaffIdRef.current !== null) {
      // Bei echtem Mitarbeiterwechsel (nicht beim ersten Setzen): Alles zurücksetzen
      setExpandedCategories({
        thisWeek: false,
        nextWeek: false,
        underControl: false
      });
      setHasAutoExpanded(false); // Erlaube neues Auto-Expand für den neuen Filter
    }
  }, [selectedStaffId, isInitialized]);

  // PERFORMANCE: Kein eigener Erstladeaufruf hier. useDeadlines lädt beim
  // Start selbst; ein zweiter Aufruf hat die Daten doppelt geholt und konnte
  // frische Klicks in der Anzeige mit älteren Daten überschreiben.

  const availableStaff = useMemo(() => {
    const staffMap = new Map<string, { id: string; name: string; initials: string }>();
    
    customerDeadlineViews.forEach(view => {
      if (view.customer.zustaendiger_mitarbeiter) {
        const staff = view.customer.zustaendiger_mitarbeiter;
        if (!staffMap.has(staff.id)) {
          staffMap.set(staff.id, {
            id: staff.id,
            name: staff.full_name,
            initials: staff.initials
          });
        }
      }
    });

    return Array.from(staffMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [customerDeadlineViews]);

  const filteredViews = useMemo(() => {
    let filtered = [...customerDeadlineViews];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(view => {
        const company = view.customer.company?.toLowerCase() || '';
        const vorname = view.customer.vorname?.toLowerCase() || '';
        const nachname = view.customer.nachname?.toLowerCase() || '';
        const imAuftragVon = view.customer.im_auftrag_von?.toLowerCase() || '';
        const auftraggeberCompany = view.customer.auftraggeber?.company?.toLowerCase() || '';
        
        return company.includes(searchLower) || 
               vorname.includes(searchLower) || 
               nachname.includes(searchLower) ||
               imAuftragVon.includes(searchLower) ||
               auftraggeberCompany.includes(searchLower);
      });
    }

    if (selectedStaffId) {
      filtered = filtered.filter(view => 
        view.customer.zustaendiger_mitarbeiter?.id === selectedStaffId
      );
    }

    return filtered;
  }, [customerDeadlineViews, searchTerm, selectedStaffId]);

  // Treffer anderer Sachbearbeiter: Bei aktiver Suche UND aktivem Mitarbeiter-Filter werden
  // zusätzlich die Suchtreffer der Kunden ermittelt, die der Mitarbeiter-Filter sonst
  // ausblendet (Kunden der Kolleginnen). Ohne Suche oder ohne Mitarbeiter-Filter bleibt
  // die Liste leer, sodass sich am bisherigen Verhalten nichts ändert.
  const otherStaffViews = useMemo(() => {
    if (!searchTerm || !selectedStaffId) {
      return [] as CustomerDeadlineView[];
    }

    const searchLower = searchTerm.toLowerCase();

    const matches = customerDeadlineViews.filter(view => {
      const company = view.customer.company?.toLowerCase() || '';
      const vorname = view.customer.vorname?.toLowerCase() || '';
      const nachname = view.customer.nachname?.toLowerCase() || '';
      const imAuftragVon = view.customer.im_auftrag_von?.toLowerCase() || '';
      const auftraggeberCompany = view.customer.auftraggeber?.company?.toLowerCase() || '';

      const matchesSearch =
        company.includes(searchLower) ||
        vorname.includes(searchLower) ||
        nachname.includes(searchLower) ||
        imAuftragVon.includes(searchLower) ||
        auftraggeberCompany.includes(searchLower);

      // Nur Kunden, die NICHT dem aktuell gefilterten Mitarbeiter gehören (keine Dopplung)
      const belongsToSelectedStaff = view.customer.zustaendiger_mitarbeiter?.id === selectedStaffId;

      return matchesSearch && !belongsToSelectedStaff;
    });

    return matches.sort(sortByNextDeadlineAndName);
  }, [customerDeadlineViews, searchTerm, selectedStaffId]);

  // Lade Zendesk-Tickets für die Treffer anderer Sachbearbeiter, sobald diese erscheinen
  useEffect(() => {
    if (otherStaffViews.length === 0) {
      return;
    }
    const signature = otherStaffViews.map(view => view.customer.id).sort().join(',');
    if (signature === otherStaffZendeskRef.current) {
      return;
    }
    otherStaffZendeskRef.current = signature;
    loadZendeskForCustomers(otherStaffViews.map(view => view.customer.id));
  }, [otherStaffViews, loadZendeskForCustomers]);

  // VERBESSERTE GRUPPIERUNG MIT KORREKTER SORTIERUNG
  // Priorität über getCustomerPriority aus useDeadlines: dieselbe vollständige
  // Berechnung wie bei der Sortierung im Hook (inklusive Erledigung der
  // QR-Fristen, deren Nachzügler und ruhender Fristen bei Wartet auf Kunden).
  const groupedCustomers = useMemo(() => {
    const groups = {
      critical: filteredViews.filter(view => {
        const priority = getCustomerPriority(view);
        return priority <= 2; // Überfällig, Heute und Morgen
      }),
      thisWeek: filteredViews.filter(view => {
        const priority = getCustomerPriority(view);
        return priority === 3;
      }),
      nextWeek: filteredViews.filter(view => {
        const priority = getCustomerPriority(view);
        return priority === 4;
      }),
      underControl: filteredViews.filter(view => {
        const priority = getCustomerPriority(view);
        return priority >= 5;
      })
    };

    // Sortiere jede Kategorie nach nächster Deadline und dann alphabetisch
    groups.critical.sort(sortByNextDeadlineAndName);
    groups.thisWeek.sort(sortByNextDeadlineAndName);
    groups.nextWeek.sort(sortByNextDeadlineAndName);
    groups.underControl.sort(sortByNextDeadlineAndName);

    return groups;
  }, [filteredViews]);

  // State um zu tracken, ob Zendesk bereits für Kategorien geladen wurde
  const [zendeskLoadedFor, setZendeskLoadedFor] = useState<{
    critical: boolean;
    thisWeek: boolean;
  }>({ critical: false, thisWeek: false });

  // Lade Zendesk-Tickets für kritische Kunden beim ersten Laden
  useEffect(() => {
    if (!zendeskLoadedFor.critical && groupedCustomers.critical.length > 0) {
      const criticalCustomerIds = groupedCustomers.critical.map(view => view.customer.id);
      loadZendeskForCustomers(criticalCustomerIds);
      setZendeskLoadedFor(prev => ({ ...prev, critical: true }));
    }
  }, [groupedCustomers.critical, loadZendeskForCustomers, zendeskLoadedFor.critical]);

  // Auto-expand erste verfügbare Gruppe wenn keine kritischen Kunden vorhanden - nur einmal!
  useEffect(() => {
    if (!loading && !hasAutoExpanded && filteredViews.length > 0 && groupedCustomers.critical.length === 0) {
      // Finde die erste Gruppe mit Kunden und klappe sie auf
      if (groupedCustomers.thisWeek.length > 0) {
        setExpandedCategories(prev => ({ ...prev, thisWeek: true }));
        setHasAutoExpanded(true);
        
        // Lade Zendesk-Tickets wenn noch nicht geladen
        if (!zendeskLoadedFor.thisWeek) {
          const thisWeekCustomerIds = groupedCustomers.thisWeek.map(view => view.customer.id);
          loadZendeskForCustomers(thisWeekCustomerIds);
          setZendeskLoadedFor(prev => ({ ...prev, thisWeek: true }));
        }
      } else if (groupedCustomers.nextWeek.length > 0) {
        setExpandedCategories(prev => ({ ...prev, nextWeek: true }));
        setHasAutoExpanded(true);
        
        // Lade Zendesk-Tickets wenn noch nicht geladen
        const nextWeekCustomerIds = groupedCustomers.nextWeek.map(view => view.customer.id);
        loadZendeskForCustomers(nextWeekCustomerIds);
      } else if (groupedCustomers.underControl.length > 0) {
        setExpandedCategories(prev => ({ ...prev, underControl: true }));
        setHasAutoExpanded(true);
        
        // Lade Zendesk-Tickets wenn noch nicht geladen
        const underControlCustomerIds = groupedCustomers.underControl.map(view => view.customer.id);
        loadZendeskForCustomers(underControlCustomerIds);
      }
    }
  }, [loading, hasAutoExpanded, filteredViews.length, groupedCustomers, loadZendeskForCustomers, zendeskLoadedFor.thisWeek]);

  // Saubere Lösung: Auto-expand Gruppen NUR bei Suchbegriffen, nicht bei Mitarbeiter-Filterung
  useEffect(() => {
    const hasSearchTerm = Boolean(searchTerm);
    const hadSearchTerm = Boolean(prevSearchTermRef.current);
    const searchTermChanged = searchTerm !== prevSearchTermRef.current;
    
    // NUR Suchbegriff-Änderungen lösen das Aufklappen ALLER Gruppen aus
    const searchWasAdded = hasSearchTerm && (!hadSearchTerm || searchTermChanged);
    
    // Alle Filter wurden entfernt
    const allFiltersRemoved = !searchTerm && !selectedStaffId && (prevSearchTermRef.current || prevSelectedStaffIdRef.current);
    
    if (searchWasAdded) {
      // Bei Suche: Alle Gruppen mit Ergebnissen aufklappen
      setExpandedCategories(prev => {
        const newState = { ...prev };
        let hasChanges = false;
        
        if (groupedCustomers.thisWeek.length > 0 && !prev.thisWeek) {
          newState.thisWeek = true;
          hasChanges = true;
          
          // Lade Zendesk-Daten falls noch nicht geladen
          if (!zendeskLoadedFor.thisWeek) {
            const thisWeekCustomerIds = groupedCustomers.thisWeek.map(view => view.customer.id);
            loadZendeskForCustomers(thisWeekCustomerIds);
            setZendeskLoadedFor(prevZ => ({ ...prevZ, thisWeek: true }));
          }
        }
        
        if (groupedCustomers.nextWeek.length > 0 && !prev.nextWeek) {
          newState.nextWeek = true;
          hasChanges = true;
        }
        
        if (groupedCustomers.underControl.length > 0 && !prev.underControl) {
          newState.underControl = true;
          hasChanges = true;
        }
        
        return hasChanges ? newState : prev;
      });
    } else if (allFiltersRemoved) {
      // Wenn alle Filter entfernt: Alles zuklappen und Auto-Expand erlauben
      setExpandedCategories({
        thisWeek: false,
        nextWeek: false,
        underControl: false
      });
      setHasAutoExpanded(false); // Erlaube Auto-Expand der ersten Gruppe
    }
    
    // Update refs für nächste Prüfung
    prevSearchTermRef.current = searchTerm;
    prevSelectedStaffIdRef.current = selectedStaffId;
  }, [searchTerm, selectedStaffId, groupedCustomers, zendeskLoadedFor.thisWeek, loadZendeskForCustomers]);

  const toggleCategory = async (category: keyof CategoryState) => {
    const newState = { ...expandedCategories, [category]: !expandedCategories[category] };
    setExpandedCategories(newState);

    if (newState[category]) {
      const customersToLoad = category === 'thisWeek' 
        ? groupedCustomers.thisWeek 
        : category === 'nextWeek' 
          ? groupedCustomers.nextWeek 
          : groupedCustomers.underControl;
      const customerIds = customersToLoad.map(view => view.customer.id);
      loadZendeskForCustomers(customerIds);
    }
  };

  const renderCategoryHeader = (
    title: string, 
    count: number, 
    isExpanded: boolean, 
    onToggle: () => void,
    isCollapsible: boolean = true
  ) => (
    <div 
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px',
        marginBottom: '8px',
        cursor: isCollapsible ? 'pointer' : 'default',
        border: '1px solid #E5E7EB'
      }}
      onClick={isCollapsible ? onToggle : undefined}
    >
      <span style={{ fontWeight: '600', color: '#374151' }}>
        {title} ({count})
      </span>
      {isCollapsible && (
        <span style={{ 
          fontSize: '12px', 
          color: '#6B7280',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>
          ▼
        </span>
      )}
    </div>
  );

  // NEUE HANDLER für Edit-Modal
  const handleEditCustomer = (customer: Customer) => {
    setCustomerToEdit(customer);
    setShowEditModal(true);
  };

  // Erfolg beim Speichern: Das Modal bleibt bewusst geöffnet, hier werden nur
  // die Daten im Hintergrund neu geladen. Das Schließen passiert ausschließlich
  // über X, Abbrechen bzw. Schließen im Modal selbst.
  const handleEditSuccess = () => {
    // Reload data to show updated customer info
    loadData();
  };

  // NEUE HANDLER für Create-Modal
  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    // Reload data to show new customer
    loadData();
  };

  // NEUE HANDLER für SecureDocument-Modal
  const handleSendDocuments = (customer: Customer) => {
    setSecureDocumentCustomer(customer);
    setShowSecureDocumentModal(true);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#6B7280'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #3B82F6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginRight: '12px'
        }} />
        Lade Kunden...
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold',
          margin: 0 
        }}>
            Fristenkalender
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#059669';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#10B981';
          }}
        >
          <span style={{ fontSize: '18px' }}>+</span>
          Neuer Kunde
        </button>
      </div>

      <DeadlineFilters
        availableStaff={availableStaff}
        selectedStaffId={selectedStaffId}
        onStaffFilterChange={setSelectedStaffId}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {(searchTerm || selectedStaffId) && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: '#EFF6FF',
          borderRadius: '6px',
          marginBottom: '16px',
          fontSize: '14px',
          color: '#1E40AF'
        }}>
          Zeige {filteredViews.length} von {customerDeadlineViews.length} Kunden
          {searchTerm && ` • Suche: "${searchTerm}"`}
          {selectedStaffId && ` • Sachbearbeiter: ${availableStaff.find(s => s.id === selectedStaffId)?.name}`}
        </div>
      )}

      {/* Stufe 3: Geplante Mandantenmails — rendert nichts, solange keine
          geplante oder fehlgeschlagene Mail existiert (null Platzverbrauch) */}
      <ScheduledFinalizationEmails />

      {/* Create Customer Modal */}
      {showCreateModal && (
        <CreateCustomerModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Edit Customer Modal */}
      {showEditModal && customerToEdit && (
        <EditCustomerModal
          isOpen={showEditModal}
          customer={customerToEdit}
          onClose={() => {
            setShowEditModal(false);
            setCustomerToEdit(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && currentFinalizationData && (
        <UploadModal
          isOpen={showUploadModal}
          customer={currentFinalizationData.view.customer}
          month={currentFinalizationData.view.currentMonth}
          year={currentFinalizationData.view.currentYear}
          todos={currentFinalizationData.view.customTodos}
          attachments={currentFinalizationData.view.customAttachments}
          onUpload={handleFileUpload}
          onClose={closeUploadModal}
        />
      )}

      {/* Release Modal (xtax_portal-Versandart, Stufe 3.1) */}
      {showReleaseModal && currentFinalizationData && (
        <ReleaseModal
          isOpen={showReleaseModal}
          customer={currentFinalizationData.view.customer}
          month={currentFinalizationData.view.currentMonth}
          year={currentFinalizationData.view.currentYear}
          todos={currentFinalizationData.view.customTodos}
          attachments={currentFinalizationData.view.customAttachments}
          onRelease={handleRelease}
          onClose={closeReleaseModal}
        />
      )}

      {/* Zendesk Warning Modal */}
      {showZendeskWarning && zendeskWarningData && (
        <ZendeskWarningModal
          isOpen={showZendeskWarning}
          customer={zendeskWarningData.view.customer}
          ticketCount={zendeskWarningData.ticketCount}
          onProceed={handleZendeskProceed}
          onCancel={handleZendeskCancel}
        />
      )}

      {/* Lohnschein-Versand Modal (Stufe 3.2).
          year/month kommen aus arbeitsjahr/arbeitsmonat des Auslösers, NICHT aus
          view.currentYear/currentMonth: Bei einer Nachzügler-Kachel gehört die
          Frist zum zuletzt abgeschlossenen Monat, nicht zum laufenden. */}
      {showLohnscheinModal && lohnscheinVersandData && (
        <LohnscheinVersandModal
          isOpen={showLohnscheinModal}
          customer={lohnscheinVersandData.view.customer}
          year={lohnscheinVersandData.arbeitsjahr}
          month={lohnscheinVersandData.arbeitsmonat}
          lohnlauf={lohnscheinVersandData.lohnlauf}
          versandJahr={lohnscheinVersandData.versandJahr}
          versandMonat={lohnscheinVersandData.versandMonat}
          onSuccess={handleLohnscheinVersandSuccess}
          onClose={closeLohnscheinModal}
        />
      )}

      {/* OPPM QR-Code-Versand Modal.
          year/month und die Abrechnungsperiode kommen bei einer Nachzügler-Kachel
          aus dem Nachzügler, sonst aus der laufenden View. Gleiche Begründung wie
          beim Lohnschein-Modal: die Frist gehört dann zum zuletzt abgeschlossenen
          Monat, nicht zum laufenden. */}
      {showOppmQrModal && oppmQrVersandData && (
        <OppmQrVersandModal
          isOpen={showOppmQrModal}
          customer={oppmQrVersandData.view.customer}
          year={oppmQrVersandData.nachzuegler?.arbeitsjahr ?? oppmQrVersandData.view.currentYear}
          month={oppmQrVersandData.nachzuegler?.arbeitsmonat ?? oppmQrVersandData.view.currentMonth}
          periodeJahr={oppmQrVersandData.nachzuegler?.periode.year ?? oppmQrVersandData.view.oppmQrPeriode?.year}
          periodeMonat={oppmQrVersandData.nachzuegler?.periode.month ?? oppmQrVersandData.view.oppmQrPeriode?.month}
          onRelease={handleOppmQrRelease}
          onMarkDone={handleOppmQrMarkDone}
          onClose={closeOppmQrModal}
        />
      )}

      {/* OPPM Monatsübersicht QR-Code-Versand Modal. Siehe Hinweis oben. */}
      {showMonatsuebersichtQrModal && monatsuebersichtQrVersandData && (
        <MonatsuebersichtQrVersandModal
          isOpen={showMonatsuebersichtQrModal}
          customer={monatsuebersichtQrVersandData.view.customer}
          year={monatsuebersichtQrVersandData.nachzuegler?.arbeitsjahr ?? monatsuebersichtQrVersandData.view.currentYear}
          month={monatsuebersichtQrVersandData.nachzuegler?.arbeitsmonat ?? monatsuebersichtQrVersandData.view.currentMonth}
          periodeJahr={monatsuebersichtQrVersandData.nachzuegler?.periode.year ?? monatsuebersichtQrVersandData.view.monatsuebersichtQrPeriode?.year}
          periodeMonat={monatsuebersichtQrVersandData.nachzuegler?.periode.month ?? monatsuebersichtQrVersandData.view.monatsuebersichtQrPeriode?.month}
          onRelease={handleMonatsuebersichtQrRelease}
          onMarkDone={handleMonatsuebersichtQrMarkDone}
          onClose={closeMonatsuebersichtQrModal}
        />
      )}

      {/* Secure Document Modal */}
      {showSecureDocumentModal && secureDocumentCustomer && (
        <SecureDocumentModal
          isOpen={showSecureDocumentModal}
          customer={secureDocumentCustomer}
          onSuccess={() => { setShowSecureDocumentModal(false); setSecureDocumentCustomer(null); }}
          onClose={() => {
            setShowSecureDocumentModal(false);
            setSecureDocumentCustomer(null);
          }}
        />
      )}
      
      {/* Kritische Kunden */}
      {groupedCustomers.critical.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          {renderCategoryHeader('🚨 Sofortiger Handlungsbedarf', groupedCustomers.critical.length, true, () => {}, false)}
          <div>
            {groupedCustomers.critical.map(view => (
              <CustomerDeadlineCard
                key={view.customer.id}
                view={view}
                onToggleStatus={toggleStatus}
                onResetMonth={resetCustomerMonth}
                onEditCustomer={handleEditCustomer}
                onSendDocuments={handleSendDocuments}
                isButtonLoading={isButtonLoading}
                onSnoozeDeadline={snoozeDeadline}
                onMarkDatenAnlieferung={markDatenAnlieferung}
                onUnmarkDatenAnlieferung={unmarkDatenAnlieferung}
                onMarkWartetAufKunden={markWartetAufKunden}
                onUnmarkWartetAufKunden={unmarkWartetAufKunden}
                onMarkKundenRueckmeldung={markKundenRueckmeldung}
                onUnmarkKundenRueckmeldung={unmarkKundenRueckmeldung}
                onOpenLohnscheinNachzuegler={openLohnscheinNachzuegler}
                onOpenSpezialfristNachzuegler={openSpezialfristNachzuegler}
                onCompleteIndividualNachzuegler={erledigeIndividualNachzuegler}
              />
            ))}
          </div>
        </div>
      )}

      {/* Diese Woche */}
      {groupedCustomers.thisWeek.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          {renderCategoryHeader(
            '📋 Diese Woche fällig', 
            groupedCustomers.thisWeek.length, 
            expandedCategories.thisWeek,
            () => toggleCategory('thisWeek')
          )}
          {expandedCategories.thisWeek && (
            <div>
              {groupedCustomers.thisWeek.map(view => (
                <CustomerDeadlineCard
                  key={view.customer.id}
                  view={view}
                  onToggleStatus={toggleStatus}
                  onResetMonth={resetCustomerMonth}
                  onEditCustomer={handleEditCustomer}
                  onSendDocuments={handleSendDocuments}
                  isButtonLoading={isButtonLoading}
                  onSnoozeDeadline={snoozeDeadline}
                  onMarkDatenAnlieferung={markDatenAnlieferung}
                  onUnmarkDatenAnlieferung={unmarkDatenAnlieferung}
                  onMarkWartetAufKunden={markWartetAufKunden}
                  onUnmarkWartetAufKunden={unmarkWartetAufKunden}
                  onMarkKundenRueckmeldung={markKundenRueckmeldung}
                  onUnmarkKundenRueckmeldung={unmarkKundenRueckmeldung}
                  onOpenLohnscheinNachzuegler={openLohnscheinNachzuegler}
                  onOpenSpezialfristNachzuegler={openSpezialfristNachzuegler}
                  onCompleteIndividualNachzuegler={erledigeIndividualNachzuegler}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Nächste Woche */}
      {groupedCustomers.nextWeek.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          {renderCategoryHeader(
            '📅 Nächste Woche fällig', 
            groupedCustomers.nextWeek.length, 
            expandedCategories.nextWeek,
            () => toggleCategory('nextWeek')
          )}
          {expandedCategories.nextWeek && (
            <div>
              {groupedCustomers.nextWeek.map(view => (
                <CustomerDeadlineCard
                  key={view.customer.id}
                  view={view}
                  onToggleStatus={toggleStatus}
                  onResetMonth={resetCustomerMonth}
                  onEditCustomer={handleEditCustomer}
                  onSendDocuments={handleSendDocuments}
                  isButtonLoading={isButtonLoading}
                  onSnoozeDeadline={snoozeDeadline}
                  onMarkDatenAnlieferung={markDatenAnlieferung}
                  onUnmarkDatenAnlieferung={unmarkDatenAnlieferung}
                  onMarkWartetAufKunden={markWartetAufKunden}
                  onUnmarkWartetAufKunden={unmarkWartetAufKunden}
                  onMarkKundenRueckmeldung={markKundenRueckmeldung}
                  onUnmarkKundenRueckmeldung={unmarkKundenRueckmeldung}
                  onOpenLohnscheinNachzuegler={openLohnscheinNachzuegler}
                  onOpenSpezialfristNachzuegler={openSpezialfristNachzuegler}
                  onCompleteIndividualNachzuegler={erledigeIndividualNachzuegler}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unter Kontrolle */}
      {groupedCustomers.underControl.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          {renderCategoryHeader(
            '✅ Unter Kontrolle', 
            groupedCustomers.underControl.length, 
            expandedCategories.underControl,
            () => toggleCategory('underControl')
          )}
          {expandedCategories.underControl && (
            <div>
              {groupedCustomers.underControl.map(view => (
                <CustomerDeadlineCard
                  key={view.customer.id}
                  view={view}
                  onToggleStatus={toggleStatus}
                  onResetMonth={resetCustomerMonth}
                  onEditCustomer={handleEditCustomer}
                  onSendDocuments={handleSendDocuments}
                  isButtonLoading={isButtonLoading}
                  onSnoozeDeadline={snoozeDeadline}
                  onMarkDatenAnlieferung={markDatenAnlieferung}
                  onUnmarkDatenAnlieferung={unmarkDatenAnlieferung}
                  onMarkWartetAufKunden={markWartetAufKunden}
                  onUnmarkWartetAufKunden={unmarkWartetAufKunden}
                  onMarkKundenRueckmeldung={markKundenRueckmeldung}
                  onUnmarkKundenRueckmeldung={unmarkKundenRueckmeldung}
                  onOpenLohnscheinNachzuegler={openLohnscheinNachzuegler}
                  onOpenSpezialfristNachzuegler={openSpezialfristNachzuegler}
                  onCompleteIndividualNachzuegler={erledigeIndividualNachzuegler}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Weitere Treffer anderer Sachbearbeiter (nur bei aktiver Suche mit Mitarbeiter-Filter) */}
      {otherStaffViews.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          {renderCategoryHeader('👥 Weitere Treffer anderer Sachbearbeiter', otherStaffViews.length, true, () => {}, false)}
          <div>
            {otherStaffViews.map(view => (
              <CustomerDeadlineCard
                key={view.customer.id}
                view={view}
                onToggleStatus={toggleStatus}
                onResetMonth={resetCustomerMonth}
                onEditCustomer={handleEditCustomer}
                onSendDocuments={handleSendDocuments}
                isButtonLoading={isButtonLoading}
                onSnoozeDeadline={snoozeDeadline}
                onMarkDatenAnlieferung={markDatenAnlieferung}
                onUnmarkDatenAnlieferung={unmarkDatenAnlieferung}
                onMarkWartetAufKunden={markWartetAufKunden}
                onUnmarkWartetAufKunden={unmarkWartetAufKunden}
                onMarkKundenRueckmeldung={markKundenRueckmeldung}
                onUnmarkKundenRueckmeldung={unmarkKundenRueckmeldung}
                onOpenLohnscheinNachzuegler={openLohnscheinNachzuegler}
                onOpenSpezialfristNachzuegler={openSpezialfristNachzuegler}
                onCompleteIndividualNachzuegler={erledigeIndividualNachzuegler}
              />
            ))}
          </div>
        </div>
      )}

      {filteredViews.length === 0 && otherStaffViews.length === 0 && (
        <div style={{ textAlign: 'center', color: '#6B7280', padding: '40px' }}>
          {searchTerm || selectedStaffId 
            ? 'Keine Kunden gefunden mit diesen Filterkriterien'
            : 'Keine Kunden gefunden'
          }
        </div>
      )}
    </div>
  );
};
